import Link from "next/link";
import { experienceDisclosure, type PublicExperienceCommerce } from "@/lib/experience-commerce";

type DisclosureCopy = {
  advertisement: string;
  commission: string;
  pageNotice: string;
  howItWorks: string;
};

function copyFor(locale?: string): DisclosureCopy {
  const language = String(locale || "en").slice(0, 2).toLowerCase();
  const copies: Record<string, DisclosureCopy> = {
    da: { advertisement: "Reklame", commission: "Teravia kan modtage en kommission, hvis du booker.", pageNotice: "Denne side indeholder udvalgte partnerlinks. Teravia kan modtage en kommission, hvis du booker.", howItWorks: "Sådan fungerer det" },
    de: { advertisement: "Werbung", commission: "Teravia kann eine Provision erhalten, wenn du buchst.", pageNotice: "Diese Seite enthält ausgewählte Partnerlinks. Teravia kann bei einer Buchung eine Provision erhalten.", howItWorks: "So funktioniert es" },
    es: { advertisement: "Publicidad", commission: "Teravia puede recibir una comisión si reservas.", pageNotice: "Esta página contiene enlaces seleccionados de socios. Teravia puede recibir una comisión si reservas.", howItWorks: "Cómo funciona" },
    fr: { advertisement: "Publicité", commission: "Teravia peut percevoir une commission si vous réservez.", pageNotice: "Cette page contient des liens sélectionnés de partenaires. Teravia peut percevoir une commission si vous réservez.", howItWorks: "Comment cela fonctionne" },
    it: { advertisement: "Pubblicità", commission: "Teravia potrebbe ricevere una commissione se prenoti.", pageNotice: "Questa pagina include link selezionati di partner. Teravia potrebbe ricevere una commissione se prenoti.", howItWorks: "Come funziona" },
    ja: { advertisement: "広告", commission: "予約すると、Teraviaが紹介料を受け取る場合があります。", pageNotice: "このページには、厳選した提携先リンクが含まれています。予約すると、Teraviaが紹介料を受け取る場合があります。", howItWorks: "仕組み" },
    nl: { advertisement: "Advertentie", commission: "Teravia kan een commissie ontvangen als je boekt.", pageNotice: "Deze pagina bevat geselecteerde partnerlinks. Teravia kan een commissie ontvangen als je boekt.", howItWorks: "Zo werkt het" },
    pt: { advertisement: "Publicidade", commission: "A Teravia pode receber uma comissão se reservar.", pageNotice: "Esta página inclui links selecionados de parceiros. A Teravia pode receber uma comissão se reservar.", howItWorks: "Como funciona" },
    sv: { advertisement: "Annons", commission: "Teravia kan få provision om du bokar.", pageNotice: "Den här sidan innehåller utvalda partnerlänkar. Teravia kan få provision om du bokar.", howItWorks: "Så fungerar det" },
  };
  return copies[language] || { advertisement: "Advertisement", commission: "Affiliate link · Teravia may earn a commission, at no extra cost to you.", pageNotice: "This page includes selected partner links. Teravia may earn a commission if you book.", howItWorks: "How this works" };
}

export function affiliatePartnerName(card: { provider?: string | null; partner_name?: string | null; url?: string | null }): string {
  if (String(card.provider || "").toLowerCase() === "viator") return "Viator";
  try {
    const host = new URL(String(card.url || "")).hostname.replace(/^www\./i, "");
    const known: Record<string, string> = {
      "tpwgts.com": "Travelpayouts",
      "travelpayouts.com": "Travelpayouts",
      "klook.com": "Klook",
      "kiwi.com": "Kiwi.com",
      "airalo.com": "Airalo",
      "localrent.com": "Localrent.com",
      "welcomepickups.com": "Welcome Pickups",
      "tiqets.com": "Tiqets",
      "gocity.com": "Go City",
      "gettransfer.com": "GetTransfer.com",
    };
    const match = Object.entries(known).find(([domain]) => host === domain || host.endsWith(`.${domain}`));
    return match?.[1] || host || String(card.partner_name || "").trim() || "Travel partner";
  } catch {
    return String(card.partner_name || "").trim() || "Travel partner";
  }
}

export function affiliateCtaLabel(partnerName: string, kind: "experience" | "option" | "flight" = "option") {
  if (kind === "flight") return `Search flights with ${partnerName}`;
  return kind === "experience" ? `View on ${partnerName}` : `Explore ${partnerName}`;
}

export function AffiliateAdvertisementLabel({ partnerName, locale, tone = "light", className = "" }: { partnerName: string; locale?: string; tone?: "light" | "dark"; className?: string }) {
  const copy = copyFor(locale);
  const label = tone === "dark" ? "text-[#ffd95a]" : "text-amber-800";
  return <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${label} ${className}`}>{copy.advertisement} · {partnerName}</p>;
}

export function AffiliateCardDisclosure({ locale, tone = "light", className = "" }: { partnerName: string; locale?: string; tone?: "light" | "dark"; className?: string }) {
  const copy = copyFor(locale);
  const subtle = tone === "dark" ? "text-slate-200" : "text-slate-600";
  const link = tone === "dark" ? "text-white hover:text-[#ffd95a]" : "text-slate-800 hover:text-slate-950";
  return (
    <p className={`text-[11px] leading-4 ${subtle} ${className}`}>
      <span className="block">{copy.commission}</span>
      <Link href="/legal/affiliate-disclosure" className={`mt-0.5 inline-block font-semibold underline underline-offset-2 ${link}`}>{copy.howItWorks}</Link>
    </p>
  );
}

export function CommercialCardDisclosure({ commerce, locale, tone = "light", className = "", context = "guide" }: { commerce: PublicExperienceCommerce; locale?: string; tone?: "light" | "dark"; className?: string; context?: "guide" | "editorial" }) {
  const disclosure = experienceDisclosure(commerce, locale, context);
  if (!disclosure) return null;
  const copy = copyFor(locale);
  const subtle = tone === "dark" ? "text-slate-200" : "text-slate-600";
  const link = tone === "dark" ? "text-white hover:text-[#ffd95a]" : "text-slate-800 hover:text-slate-950";
  return (
    <p className={`text-[11px] leading-4 ${subtle} ${className}`}>
      <span className="block">{disclosure}</span>
      <Link href="/legal/affiliate-disclosure" className={`mt-0.5 inline-block font-semibold underline underline-offset-2 ${link}`}>{copy.howItWorks}</Link>
    </p>
  );
}

export function AffiliatePageDisclosure({ locale, tone = "light", className = "" }: { locale?: string; tone?: "light" | "dark"; className?: string }) {
  const copy = copyFor(locale);
  const text = tone === "dark" ? "text-slate-200" : "text-slate-600";
  const link = tone === "dark" ? "text-white hover:text-[#ffd95a]" : "text-slate-800 hover:text-slate-950";
  return <p className={`text-xs leading-5 ${text} ${className}`}>{copy.pageNotice} <Link href="/legal/affiliate-disclosure" className={`font-semibold underline underline-offset-2 ${link}`}>{copy.howItWorks}</Link></p>;
}
